/**
 * useChessTimer - Timer management for chess games
 *
 * Handles:
 * - Client-side timer countdown
 * - Server time sync
 * - Low time warnings
 * - Time formatting
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ChessGame, ChessColor } from '../types';

const LOW_TIME_THRESHOLD = 30000; // 30 seconds
const TICK_INTERVAL = 100; // Update every 100ms for smooth countdown
const SERVER_SYNC_INTERVAL = 3000; // Sync with server every 3 seconds

export interface UseChessTimerOptions {
    game: ChessGame | null;
    isOngoing: boolean;
    checkTime: () => void;
}

export interface PlayerTime {
    timeLeft: number;
    formatted: string;
    isLow: boolean;
    isActive: boolean;
}

export interface UseChessTimerReturn {
    whiteTime: PlayerTime;
    blackTime: PlayerTime;
    activeColor: ChessColor | null;
}

function formatTime(ms: number): string {
    if (ms <= 0) return '0:00';

    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function useChessTimer({
    game,
    isOngoing,
    checkTime
}: UseChessTimerOptions): UseChessTimerReturn {
    // Track local time adjustments for smooth countdown
    const [localTimeOffset, setLocalTimeOffset] = useState(0);
    const lastTickRef = useRef<number>(Date.now());
    const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Get players
    const whitePlayer = useMemo(() =>
        game?.players.find(p => p.color === ChessColor.PLAIN),
    [game?.players]);

    const blackPlayer = useMemo(() =>
        game?.players.find(p => p.color === ChessColor.CHARCOAL),
    [game?.players]);

    // Determine active color (whose turn it is)
    const activeColor = useMemo((): ChessColor | null => {
        if (!game || !isOngoing) return null;

        const currentPlayerObj = game.players.find(p => p.id === game.currentPlayer);
        return currentPlayerObj?.color || null;
    }, [game, isOngoing]);

    // Client-side tick for smooth countdown
    useEffect(() => {
        if (!isOngoing || !activeColor) {
            // Clear interval when game not ongoing
            if (tickIntervalRef.current) {
                clearInterval(tickIntervalRef.current);
                tickIntervalRef.current = null;
            }
            setLocalTimeOffset(0);
            return;
        }

        lastTickRef.current = Date.now();

        tickIntervalRef.current = setInterval(() => {
            const now = Date.now();
            const elapsed = now - lastTickRef.current;
            lastTickRef.current = now;

            // Accumulate offset (will be reset on server sync)
            setLocalTimeOffset(prev => prev + elapsed);
        }, TICK_INTERVAL);

        return () => {
            if (tickIntervalRef.current) {
                clearInterval(tickIntervalRef.current);
                tickIntervalRef.current = null;
            }
        };
    }, [isOngoing, activeColor]);

    // Server sync interval
    useEffect(() => {
        if (!isOngoing) {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
            return;
        }

        syncIntervalRef.current = setInterval(() => {
            checkTime();
            // Reset local offset on sync
            setLocalTimeOffset(0);
            lastTickRef.current = Date.now();
        }, SERVER_SYNC_INTERVAL);

        return () => {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
        };
    }, [isOngoing, checkTime]);

    // Calculate times with local offset
    const calculateTime = useCallback((player: typeof whitePlayer, isActive: boolean): PlayerTime => {
        if (!player) {
            return {
                timeLeft: 0,
                formatted: '--:--',
                isLow: false,
                isActive: false
            };
        }

        // Apply local offset only to active player
        const adjustedTime = isActive
            ? Math.max(0, player.timeLeft - localTimeOffset)
            : player.timeLeft;

        return {
            timeLeft: adjustedTime,
            formatted: formatTime(adjustedTime),
            isLow: adjustedTime <= LOW_TIME_THRESHOLD && adjustedTime > 0,
            isActive
        };
    }, [localTimeOffset]);

    const whiteTime = useMemo(() =>
        calculateTime(whitePlayer, activeColor === ChessColor.PLAIN),
    [whitePlayer, activeColor, calculateTime]);

    const blackTime = useMemo(() =>
        calculateTime(blackPlayer, activeColor === ChessColor.CHARCOAL),
    [blackPlayer, activeColor, calculateTime]);

    return {
        whiteTime,
        blackTime,
        activeColor
    };
}

// Export formatTime for use in other components
export { formatTime };
